import { useState } from 'react'
import z from 'zod'
import {
  approvalTypeSchema,
  PaymentRulesConfig,
  targetTypeSchema,
} from '../schema'

// Define the "All" constant
export const ALL_FILTER = 'All' as const
export type AllFilter = typeof ALL_FILTER

export const targetTypeFilterSchema = z.union([
  z.literal(ALL_FILTER),
  targetTypeSchema,
])
export const approvalTypeFilterSchema = z.union([
  z.literal(ALL_FILTER),
  approvalTypeSchema,
])
export const statusFilterSchema = z.union([
  z.literal(ALL_FILTER),
  z.literal('active'),
  z.literal('inactive'),
])

export type TargetTypeFilter = z.infer<typeof targetTypeFilterSchema>
export type ApprovalTypeFilter = z.infer<typeof approvalTypeFilterSchema>
export type StatusFilter = z.infer<typeof statusFilterSchema>

export interface FilterValues {
  targetType: TargetTypeFilter
  approvalType: ApprovalTypeFilter
  status: StatusFilter
}

export interface FilterActions {
  setTargetType: (targetType: TargetTypeFilter) => void
  setApprovalType: (approvalType: ApprovalTypeFilter) => void
  setStatus: (value: StatusFilter) => void
}

export interface ApprovalRuleFilters {
  values: FilterValues
  actions: FilterActions
}

const DEFAULT_FILTERS: FilterValues = {
  targetType: ALL_FILTER,
  approvalType: ALL_FILTER,
  status: 'All',
}

type RuleFilters = {
  targetType: {
    id: number
    name: TargetTypeFilter
  }[]
  approvalType: {
    id: number
    name: ApprovalTypeFilter
  }[]
  status: {
    id: number
    name: StatusFilter
  }[]
}

export function createFilterOptions(
  approvalRulesConfig: PaymentRulesConfig
): RuleFilters {
  return {
    targetType: [
      { id: 0, name: 'All' },
      ...approvalRulesConfig.rule_target_types,
    ],
    approvalType: [
      { id: 0, name: 'All' },
      ...approvalRulesConfig.approval_types,
    ],
    status: [
      { id: 0, name: 'All' },
      { id: 1, name: 'active' },
      { id: 2, name: 'inactive' },
    ],
  }
}

export function useApprovalRuleFilters() {
  const [filters, setFilters] = useState<FilterValues>(DEFAULT_FILTERS)

  const setTargetType = (value: TargetTypeFilter) => {
    setFilters((prev) => ({ ...prev, targetType: value }))
  }

  const setApprovalType = (value: ApprovalTypeFilter) => {
    setFilters((prev) => ({ ...prev, approvalType: value }))
  }

  const setStatus = (value: StatusFilter) => {
    setFilters((prev) => ({ ...prev, status: value }))
  }

  return {
    values: filters,
    actions: {
      setTargetType,
      setApprovalType,
      setStatus,
    },
  }
}
